import { Divider, Stack, SxProps, Theme, useTheme } from '@mui/material'
import { useCallback, useMemo } from 'react'

import FormikLabeledTextField from '~/components/formikFields/formikLabeledTextField'
import { ENTER_KEY } from '~/constants'
import {
  getPreChatFormFieldLabel,
  getPreChatFormFieldPlaceholder,
} from '~/features/chatbotV2/components/dialog/dialogInput/preChatForm/utils'
import {
  EFormFieldDataType,
  EPreChatFormFieldKeys,
  TGuestPreChatFormField,
} from '~/features/preChatForms/models'
import { buildPreChatFormFieldLayout } from '~/features/preChatForms/utils'
import rem from '~/utils/ui'

type TPreChatFormFieldProps = {
  field: TGuestPreChatFormField
  onFieldEnterKeyDown: () => void
  sx?: SxProps<Theme>
}

function PreChatFormField({
  field,
  onFieldEnterKeyDown,
  sx = {},
}: TPreChatFormFieldProps) {
  const fieldMapping = field[EPreChatFormFieldKeys.fieldMapping]
  const dataType = field[EPreChatFormFieldKeys.dataType]
  const isRequired = field[EPreChatFormFieldKeys.required]

  const handleFieldKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== ENTER_KEY && event.code !== ENTER_KEY) {
        return
      }

      event.preventDefault()
      onFieldEnterKeyDown()
    },
    [onFieldEnterKeyDown],
  )

  const meta = useMemo(() => ({ handleFieldKeyDown }), [handleFieldKeyDown])

  return (
    <FormikLabeledTextField
      label={getPreChatFormFieldLabel(field)}
      meta={meta}
      name={fieldMapping}
      onKeyDown={handleFieldKeyDown}
      placeholder={getPreChatFormFieldPlaceholder(field)}
      required={isRequired}
      sx={{
        paddingTop: rem(20),
        paddingBottom: rem(4),
        '&:has(.MuiFormLabel-root[data-shrink=false])': {
          paddingBlock: rem(12),
        },
        '.MuiFormLabel-root': {
          '&[data-shrink=false]': {
            translate: '0px -9px',
          },
          '&[data-shrink=true]': {
            translate: '0px -7px',

            '+ .MuiInputBase-root': {
              'input:placeholder-shown::placeholder': {
                opacity: 0,
              },
            },
          },
        },
        '.MuiOutlinedInput-notchedOutline': {
          border: 'none',
        },
        '.MuiInputBase-input': {
          paddingBlock: 0,
        },
        '.MuiFormHelperText-root': {
          marginTop: 0,
          borderTop: 1,
        },
        ...sx,
      }}
      withTextFieldLabel
      type={dataType === EFormFieldDataType.email ? 'email' : 'text'}
    />
  )
}

type TProps = {
  onFieldEnterKeyDown: () => void
  sortedFields: TGuestPreChatFormField[]
}

function PreChatForm({ onFieldEnterKeyDown, sortedFields }: TProps) {
  const theme = useTheme()
  const fieldLayout = useMemo(
    () => buildPreChatFormFieldLayout(sortedFields),
    [sortedFields],
  )

  return (
    <Stack
      sx={{
        borderRadius: rem(12),
        border: 1,
        borderColor: theme.palette.divider,
        borderBottom: 0,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        backgroundColor: theme.palette.common.white,
      }}
      divider={<Divider />}
    >
      {fieldLayout.map((item) => {
        if (item.type === 'field') {
          const fieldId = item.field[EPreChatFormFieldKeys.id]

          return (
            <PreChatFormField
              key={fieldId}
              field={item.field}
              onFieldEnterKeyDown={onFieldEnterKeyDown}
            />
          )
        }

        return (
          <Stack key="name-row" direction="row" divider={<Divider />}>
            <PreChatFormField
              field={item.firstName}
              onFieldEnterKeyDown={onFieldEnterKeyDown}
              sx={{ flex: 1, minWidth: 0 }}
            />
            <PreChatFormField
              field={item.lastName}
              onFieldEnterKeyDown={onFieldEnterKeyDown}
              sx={{ flex: 1, minWidth: 0 }}
            />
          </Stack>
        )
      })}
    </Stack>
  )
}

export default PreChatForm
